import { useMemo } from "react";

import { ConsentManagementConfigVersion, useApiQuery } from "@/api";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import {
  ConsentManagementSlug,
  consentManagementIntegrations,
} from "@/consent-management/consentManagement";
import { useConsentCategorizableDestinations } from "@/consent-management/hooks/useConsentCategorizableDestinations";
import { useHasFreshpaintConsentManagerEntitlement } from "@/consent-management/useHasConsentManagerEntitlement";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { useHasSessionReplayHeatmapsEntitlement } from "@/sessionReplay/useHasSessionReplayHeatmapsEntitlement";
import { LoadedConsentManagementInfo } from "@/types/global";
import { findOtherLiveConfig } from "./draftStatus";
import { ConsentDraftState } from "./targeting";

export const freshpaintConsent = consentManagementIntegrations.freshpaint;

type Configs = Partial<
  Record<ConsentManagementSlug, LoadedConsentManagementInfo>
>;

export type FreshpaintConsentState = {
  isLoading: boolean;
  /**
   * The configs, versions or destinations couldn't load, so nothing is known
   * about them.
   */
  loadError: boolean;
  retry: () => void;
  hasEntitlement: boolean;
  /** Null while permissions load. */
  canWrite: boolean | null;
  audiencesEnabled: boolean;
  sessionReplayEntitled: boolean;
  config: LoadedConsentManagementInfo | undefined;
  draft: ConsentManagementConfigVersion | undefined;
  lastPublished: ConsentManagementConfigVersion | undefined;
  /** Another vendor's config that already governs this environment. */
  otherLiveConfig: LoadedConsentManagementInfo | undefined;
  destinations: ReturnType<typeof useConsentCategorizableDestinations>;
  /** Refetches the versions so a save starts from the server's latest draft. */
  fetchLatestDraft: () => Promise<ConsentManagementConfigVersion | undefined>;
  fetchOtherLiveConfig: () => Promise<LoadedConsentManagementInfo | undefined>;
};

function useConsentConfigQueries() {
  const configsQuery = useApiQuery(["get_consent_management_configs", {}]);
  const configs: Configs = configsQuery.data?.configs ?? {};
  const config = configs[freshpaintConsent.slug];
  const versionsQuery = useApiQuery(
    [
      "get_consent_management_config_versions",
      { type: freshpaintConsent.slug },
    ],
    { enabled: !!config },
  );
  return { configsQuery, configs, config, versionsQuery };
}

const findDraft = (versions: ConsentManagementConfigVersion[] = []) =>
  versions.find((version) => !version.publishedAt);

export function useConsentDraftState(): ConsentDraftState {
  const { configsQuery, config, versionsQuery } = useConsentConfigQueries();
  const known = configsQuery.isSuccess && (!config || versionsQuery.isSuccess);
  const draft = known ? findDraft(versionsQuery.data?.versions) : undefined;
  return useMemo(() => ({ draft, known }), [draft, known]);
}

export function useFreshpaintConsent(): FreshpaintConsentState {
  const { configsQuery, configs, config, versionsQuery } =
    useConsentConfigQueries();
  const entitlement = useHasFreshpaintConsentManagerEntitlement();
  const sessionReplay = useHasSessionReplayHeatmapsEntitlement();
  const flag = useFeatureFlags();
  const canWrite = useUserHasAccess(
    RBACDomain.consentManagement,
    RBACAccessLevel.write,
  );
  const destinationsQuery = useListAllDestinationsForEnv();
  const destinations = useConsentCategorizableDestinations();

  const versions = versionsQuery.data?.versions ?? [];
  return {
    isLoading:
      configsQuery.isLoading ||
      (!!config && versionsQuery.isLoading) ||
      destinationsQuery.isLoading ||
      entitlement.loading ||
      sessionReplay.loading,
    loadError:
      configsQuery.isError ||
      (!!config && versionsQuery.isError) ||
      destinationsQuery.isError,
    retry: () => {
      void configsQuery.refetch();
      void destinationsQuery.refetch();
      if (config) {
        void versionsQuery.refetch();
      }
    },
    // An entitlement lookup failure shouldn't dead-end a paying customer.
    hasEntitlement: entitlement.hasEntitlement || entitlement.isError,
    canWrite,
    audiencesEnabled: flag("audiences"),
    sessionReplayEntitled: sessionReplay.hasEntitlement,
    config,
    draft: findDraft(versions),
    lastPublished: versions.find((version) => version.publishedAt),
    otherLiveConfig: findOtherLiveConfig(configs),
    destinations,
    fetchLatestDraft: async () => {
      const { data } = await versionsQuery.refetch({ throwOnError: true });
      return findDraft(data?.versions);
    },
    fetchOtherLiveConfig: async () => {
      const { data } = await configsQuery.refetch({ throwOnError: true });
      return findOtherLiveConfig(data?.configs ?? {});
    },
  };
}

export const NO_WRITE_ACCESS_REASON =
  "Ask an admin for permission to edit consent management";

export const LOAD_ERROR_REASON =
  "Your consent settings didn’t load. Try again above.";
